import { getSupabase } from '@/lib/supabase';

import { ApiError, toApiError } from '../ApiError';

import {
  type Accessory,
  type AccessoryFacets,
  type AccessoryListFilters,
  type AccessorySort,
  type AccessorySource,
  type AccessoryStock,
  type Page,
} from './types';

const MEDIA_BUCKET = 'accessory-media';
const DEFAULT_PAGE_SIZE = 12;

/** Row shape of public.public_accessories. */
type AccessoryRow = {
  id: string;
  slug: string;
  name: string;
  category_slug: string;
  category_name: string;
  brand: string;
  source: AccessorySource;
  /** The database spells these with underscores; the site has always used hyphens. */
  stock_state: 'in_stock' | 'on_order' | 'made_to_order';
  is_universal: boolean;
  fitment_makes: string[] | null;
  fitment_note: string | null;
  price_ugx: number | string | null;
  lead_time_min_days: number | null;
  lead_time_max_days: number | null;
  summary: string;
  description: string | null;
  specs: { label: string; value: string }[] | null;
  includes: string[] | null;
  cover_storage_path: string | null;
  cover_alt_text: string | null;
  cover_width: number | null;
  cover_height: number | null;
  is_featured: boolean;
  published_at: string;
  updated_at: string;
};

/** Row shape of public.public_accessory_facets — one row, three arrays. */
type FacetRow = {
  categories: { slug: string; name: string; blurb: string | null; count: number }[] | null;
  brands: string[] | null;
  makes: string[] | null;
};

const STOCK: Record<AccessoryRow['stock_state'], AccessoryStock> = {
  in_stock: 'in-stock',
  on_order: 'on-order',
  made_to_order: 'made-to-order',
};

/**
 * How each sort reaches SQL.
 *
 * `popular` is featured-first then alphabetical, which is what the mock did and
 * what the label promises — "most asked for" is a curation decision Kabex makes
 * with the featured flag, not something the data can infer.
 *
 * Both price orders put `nullsFirst: false`, so a quote-only part sorts LAST in
 * either direction. That is not a quirk to tidy away: a part with no price is
 * not a cheap one and not an expensive one, and either coercion would put it
 * somewhere that reads as a claim about its cost.
 */
const SORTS: Record<AccessorySort, { column: string; ascending: boolean }> = {
  popular: { column: 'is_featured', ascending: false },
  'price-asc': { column: 'price_ugx', ascending: true },
  'price-desc': { column: 'price_ugx', ascending: false },
  'name-asc': { column: 'name', ascending: true },
};

/** PostgREST returns numeric columns as strings to preserve precision. */
function num(value: number | string | null): number | null {
  if (value === null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function publicMediaUrl(path: string): string {
  return getSupabase().storage.from(MEDIA_BUCKET).getPublicUrl(path).data.publicUrl;
}

function toAccessory(row: AccessoryRow): Accessory {
  const makes = row.fitment_makes ?? [];
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    category: row.category_slug,
    categoryLabel: row.category_name,
    brand: row.brand,
    source: row.source,
    stock: STOCK[row.stock_state],
    fitment: {
      universal: row.is_universal,
      // Empty when universal — a part that fits everything names no cars, and
      // the database refuses rows that try to do both.
      makes: row.is_universal ? [] : makes,
      note: row.fitment_note,
    },
    priceUgx: num(row.price_ugx),
    leadTimeDays:
      row.lead_time_min_days === null || row.lead_time_max_days === null
        ? null
        : { min: row.lead_time_min_days, max: row.lead_time_max_days },
    summary: row.summary,
    description: row.description ?? '',
    specs: row.specs ?? [],
    includes: row.includes ?? [],
    cover: row.cover_storage_path
      ? {
          url: publicMediaUrl(row.cover_storage_path),
          alt: row.cover_alt_text,
          width: row.cover_width,
          height: row.cover_height,
        }
      : null,
    isFeatured: row.is_featured,
    updatedAt: row.updated_at,
  };
}

/**
 * A term, made safe for a PostgREST `or=` expression.
 *
 * `%` and `_` are `ilike` wildcards, and a comma, parenthesis or double quote
 * terminates or re-opens a filter group — all of them are punctuation a shopper
 * can legitimately type, and none may be allowed to change what the query
 * means. Replaced with spaces rather than stripped, so "GT-R" narrows to
 * "GT R" instead of silently becoming "GTR".
 */
function searchTerm(q: string): string | null {
  const term = q.replace(/[%_,()"']/g, ' ').trim();
  return term ? term : null;
}

/** Same rule, for the one value that reaches an array-contains filter. */
function makeFilter(make: string): string | null {
  const name = make.replace(/[{},"\\]/g, ' ').trim();
  return name ? name : null;
}

export async function listAccessories(
  filters: AccessoryListFilters = {},
): Promise<Page<Accessory>> {
  const page = Math.max(1, filters.page ?? 1);
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
  const sort = SORTS[filters.sort ?? 'popular'];
  const from = (page - 1) * pageSize;

  let query = getSupabase()
    .from('public_accessories')
    .select('*', { count: 'exact' })
    .order(sort.column, { ascending: sort.ascending, nullsFirst: false })
    // The tie-break, so a page boundary is stable between requests: without it
    // two parts with the same price can swap places and one of them is then
    // shown twice or not at all.
    .order('name', { ascending: true })
    .range(from, from + pageSize - 1);

  if (filters.category) query = query.eq('category_slug', filters.category);
  if (filters.source) query = query.eq('source', filters.source);

  if (filters.make) {
    const make = makeFilter(filters.make);
    // A malformed make cannot match any part.
    if (make === null) return { items: [], total: 0, page, pageSize };
    // A universal part fits this car TOO, which is the whole reason this is an
    // `or` and not an array test: filtering by equality alone hides every floor
    // mat and every battery the moment a shopper picks their car.
    query = query.or(`is_universal.eq.true,fitment_makes.cs.{"${make}"}`);
  }

  if (filters.q) {
    const term = searchTerm(filters.q);
    if (term === null) return { items: [], total: 0, page, pageSize };
    query = query.or(`name.ilike.%${term}%,brand.ilike.%${term}%,summary.ilike.%${term}%`);
  }

  try {
    const { data, error, count } = await query.returns<AccessoryRow[]>();
    if (error) throw error;
    return { items: data.map(toAccessory), total: count ?? 0, page, pageSize };
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getAccessoryBySlug(slug: string): Promise<Accessory> {
  let row: AccessoryRow | null;
  try {
    const { data, error } = await getSupabase()
      .from('public_accessories')
      .select('*')
      .eq('slug', slug)
      .maybeSingle<AccessoryRow>();
    if (error) throw error;
    row = data;
  } catch (error) {
    throw toApiError(error);
  }
  if (!row) throw new ApiError('not_found', 'We could not find that accessory.');
  return toAccessory(row);
}

/**
 * Parts a visitor looking at this one might also need.
 *
 * Same shelf first, then anything that fits the same cars, and never the part
 * itself — then whatever is featured, rather than an empty rail. A related
 * strip that sometimes disappears makes the page look broken.
 *
 * Two requests in the common case — the subject, then its shelf — and four at
 * worst. The subject is fetched again rather than passed in: React Query caches
 * it under a different key from the detail page's copy, so this layer cannot
 * borrow it without reaching into the cache, and reaching into the cache from a
 * service is how a service stops being testable. One extra round trip on a page
 * that shows one part is the right side of that trade.
 *
 * The ranking is here rather than in SQL deliberately: a database function
 * would hide "same shelf, then same cars, then anything" behind a name, and it
 * is a merchandising decision that will be argued about.
 */
export async function listRelatedAccessories(slug: string, limit = 3): Promise<Accessory[]> {
  const subject = await getAccessoryBySlug(slug).catch(() => null);
  if (!subject) return [];

  const picked = new Map<string, Accessory>();
  const take = (items: Accessory[]) => {
    for (const item of items) {
      if (item.slug !== slug && picked.size < limit) picked.set(item.slug, item);
    }
  };

  const sameShelf = await listAccessories({
    category: subject.category,
    pageSize: limit + 1,
  });
  take(sameShelf.items);

  if (picked.size < limit && subject.fitment.makes.length > 0) {
    const sameCars = await listAccessories({
      make: subject.fitment.makes[0],
      pageSize: limit + 1,
    });
    take(sameCars.items.filter((a) => a.category !== subject.category));
  }

  if (picked.size < limit) {
    const anything = await listAccessories({ pageSize: limit + 1 });
    take(anything.items);
  }

  return [...picked.values()];
}

/**
 * What the filters may offer.
 *
 * One request against a view that does the counting, because PostgREST cannot
 * express `distinct` or `group by` — and because the counts have to come from
 * the whole catalogue rather than the current result set: a shopper who has
 * narrowed to one shelf still needs to see what switching to another would give
 * them.
 *
 * Zero-count shelves are dropped HERE rather than on the page. An option that
 * returns an empty grid is a dead end wearing the clothes of a choice, and the
 * view deliberately returns every active shelf so this layer can make that call
 * while Kabex Hub still shows the empty one.
 */
export async function getAccessoryFacets(): Promise<AccessoryFacets> {
  let row: FacetRow | null;
  try {
    const { data, error } = await getSupabase()
      .from('public_accessory_facets')
      .select('*')
      .maybeSingle<FacetRow>();
    if (error) throw error;
    row = data;
  } catch (error) {
    throw toApiError(error);
  }

  return {
    categories: (row?.categories ?? [])
      .filter((c) => c.count > 0)
      .map((c) => ({ value: c.slug, label: c.name, blurb: c.blurb, count: c.count })),
    makes: row?.makes ?? [],
    brands: row?.brands ?? [],
  };
}
